import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'store';
import { useNavigate, useLocation } from 'react-router-dom';

import { TextField, MenuItem, InputAdornment } from '@mui/material';
import { IconBuildingHospital } from '@tabler/icons';
import { some } from 'lodash';

import { setClinic } from 'store/slices/account';
import { resetState as resetPatientListState } from 'store/slices/patientList';
import { resetState as resetVitalsAlertsState } from 'store/slices/vitalsAlerts';
import { resetState as resetTaskState } from 'store/slices/task';
import { resetClinicMembersList, resetClinicManagerOrHigherMembersList } from 'store/slices/provider';
import { resetTemplateListState } from 'store/slices/protocolsInstance';
import { resetSelectedMenuItem } from 'store/slices/menu';
import { resetTwilioOutboundCallingConfigState } from 'store/slices/twilioOutboundCallingConfig';
import { resetClinicFeaturesState } from 'store/slices/clinicFeatures';
import useTwilioCall from 'hooks/useTwilioCall';
import getSortedClinicList from 'utils/clinicList';

const GLOBAL_PAGES = [
    /^\/users/,
    /^\/user\/[0-9]+\/detail/,
    /^\/protocols\/template/,
    /^\/custom-programmatic-task/,
    /^\/organization-teams-management/,
    /^\/productivity-goals/,
    /^\/team-performance/,
    /^\/import-report-submission-management/
];

function getPageClinicChangeNavigationDestination(newClinicId, currentPath) {
    if (/\/clinic\/[0-9]+\/details/.test(currentPath)) {
        return `/clinic/${newClinicId}/details`;
    }
    if (/\/clinic\/[0-9]+\/reports/.test(currentPath)) {
        return `/clinic/${newClinicId}/reports`;
    }
    if (/\/clinic\/[0-9]+\/programmatic-tasks/.test(currentPath)) {
        return `/clinic/${newClinicId}/programmatic-tasks`;
    }
    if (/\/clinic\/[0-9]+\/importer-tool/.test(currentPath)) {
        return `/clinic/${newClinicId}/importer-tool`;
    }

    return '/';
}

export default function ClinicSelection() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const accountData = useSelector((state) => state.account);
    const { isActive: isCallActive } = useTwilioCall();
    const [onClinicLevelPage, setOnClinicLevelPage] = useState(true);

    const clinicList = getSortedClinicList(accountData.clinicGrants);

    const handleChange = (event) => {
        if (event.target.value !== accountData.selectedClinic) {
            dispatch(setClinic(event.target.value));
            // NOTE: these slices are shared buckets, not keyed by clinic, and only reset here on
            // the dropdown switch path. Direct URL nav / browser back-forward between clinics
            // bypasses this handler, so a slice can briefly serve the previous clinic's data until
            // its fetch resolves. Most consumers hide this by gating on the slice's `loaded` flag;
            // the clinic-features consumer (patient list) reads `outboundCallingEnabled` directly,
            // so its stale window is user-visible (a column flashes on/off). Fix when it matters by
            // keying those slices by clinicId.
            resetPatientListState();
            resetVitalsAlertsState();
            resetSelectedMenuItem();
            resetTaskState();
            resetTemplateListState();
            resetClinicMembersList();
            resetClinicManagerOrHigherMembersList();
            resetTwilioOutboundCallingConfigState();
            resetClinicFeaturesState();
            navigate(getPageClinicChangeNavigationDestination(event.target.value, location.pathname));
        }
    };

    useEffect(() => {
        setOnClinicLevelPage(!some(GLOBAL_PAGES, (page) => page.test(location.pathname)));
    }, [location.pathname]);

    return (
        <>
            {onClinicLevelPage && clinicList.length >= 1 && accountData.selectedClinic !== '0' && (
                <TextField
                    select
                    size="small"
                    value={accountData.selectedClinic}
                    onChange={handleChange}
                    label="Clinic Selection"
                    disabled={isCallActive}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconBuildingHospital />
                            </InputAdornment>
                        )
                    }}
                >
                    {clinicList.map((clinic) => (
                        <MenuItem key={clinic.id} value={`${clinic.id}`}>
                            {' '}
                            {clinic.name}
                        </MenuItem>
                    ))}
                </TextField>
            )}
        </>
    );
}
